/**
 * PHYSICAL-RISK-SCALE-COLORS
 * Couleurs partagees par les representations d'une echelle (echelle en liste, entonnoir de prevention)
 */
import { PhysicalRiskScaleItem } from "../model/physical-risk.types"
import getColor from "@/utils/get-color.utils"

interface GradientStop {
  pos: number
  color: number[]
}

function hexToRgb(hex: string): number[] {
  return [1, 3, 5].map((index) => Number.parseInt(hex.slice(index, index + 2), 16))
}

/** Eclaircit une couleur vers le blanc, sans changer sa teinte. */
function lighten(color: number[], amount: number): number[] {
  return color.map((channel) => Math.round(channel + (255 - channel) * amount))
}

//Gravite, frequence, risque : du vert au rouge
const RISK_STOPS: GradientStop[] = [
  { pos: 0, color: [32, 202, 126] },
  { pos: 0.4, color: [242, 214, 0] },
  { pos: 0.7, color: [255, 149, 0] },
  { pos: 1, color: [235, 90, 70] }
]

//Prevention : nuances construites autour du bleu du design system. Plus le niveau est eleve, plus
//getColor("blue") est franc. Le gris reste reserve aux niveaux non atteints.
const PREVENTION_BLUE = hexToRgb(getColor("blue"))
const PREVENTION_STOPS: GradientStop[] = [
  { pos: 0, color: lighten(PREVENTION_BLUE, 0.65) },
  { pos: 0.5, color: lighten(PREVENTION_BLUE, 0.35) },
  { pos: 1, color: PREVENTION_BLUE }
]

/** Interpole la couleur du gradient a une position entre 0 et 1 */
function interpolate(stops: GradientStop[], position: number): string {
  for (let i = 0; i < stops.length - 1; i++) {
    const start = stops[i]
    const end = stops[i + 1]
    if (position >= start.pos && position <= end.pos) {
      const ratio = (position - start.pos) / (end.pos - start.pos)
      const [r, g, b] = start.color.map((channel, index) => Math.round(channel + (end.color[index] - channel) * ratio))
      return `rgb(${r}, ${g}, ${b})`
    }
  }
  const [r, g, b] = stops[stops.length - 1].color
  return `rgb(${r}, ${g}, ${b})`
}

/** Calcule une couleur approximative du gradient. */
export function getGradientColorAt(position: number): string {
  return interpolate(RISK_STOPS, position)
}

/** Degrade de bleu des niveaux de prevention atteints. */
export function getPreventionColorAt(position: number): string {
  return interpolate(PREVENTION_STOPS, position)
}

/** Meme couleur, desaturee : sert de fond a un element selectionne. interpolate rend un "rgb(...)". */
export function withAlpha(color: string, alpha: number): string {
  return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`)
}

/** Position d'une valeur dans son echelle, entre 0 et 1 : sert a piocher la couleur du gradient */
export function getScalePosition(scale: readonly PhysicalRiskScaleItem[], value: number): number {
  const index = scale.findIndex((scaleItem) => scaleItem.value === value)
  return index === -1 ? 0 : index / Math.max(1, scale.length - 1)
}
